import { Box } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew';
import { PointerEvent, RefObject, useCallback, useEffect, useRef } from 'react';

type Props = {
  value: number;
  onChange: (value: number) => void;
  containerRef: RefObject<HTMLDivElement | null>;
};

const SliderHandler = ({ value, containerRef, onChange }: Props) => {
  const isDragging = useRef(false);
  const handleRef = useRef<HTMLDivElement>(null);

  const handleMove = useCallback(
    (clientX: number) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percentage = (x / rect.width) * 100;
      onChange(percentage);
    },
    [containerRef, onChange],
  );

  useEffect(() => {
    const el = handleRef.current;
    if (!el) return;

    const stopNativePropagation = (e: Event) => e.stopPropagation();

    el.addEventListener('touchstart', stopNativePropagation, {
      passive: false,
    });
    el.addEventListener('touchend', stopNativePropagation);

    return () => {
      el.removeEventListener('touchstart', stopNativePropagation);
      el.removeEventListener('touchend', stopNativePropagation);
    };
  }, []);

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    isDragging.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    e.preventDefault();
    handleMove(e.clientX);
  };

  const onPointerUp = (e: PointerEvent<HTMLDivElement>) => {
    isDragging.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: '2px',
        bgcolor: 'common.white',
        left: `${value}%`,
        transform: 'translateX(-50%)',
        boxShadow: 3,
        pointerEvents: 'none',
        zIndex: 10,
      }}
    >
      <Box
        ref={handleRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 48,
          height: 48,
          bgcolor: 'common.white',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: 4,
          pointerEvents: 'auto',
          cursor: 'ew-resize',
          touchAction: 'none',
        }}
      >
        <ArrowBackIcon />
        <ArrowForwardIcon />
      </Box>
    </Box>
  );
};

export default SliderHandler;
